package com.crowpay.utils

import com.crowpay.circle
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.views.screens.common.ActionImportance
import com.crowpay.views.theming.AppColors
import com.crowpay.views.theming.PrimaryButtonSemantic
import com.crowpay.views.theming.body
import com.crowpay.views.theming.specCenteredContent
import com.lightningkite.kiteui.models.Action
import com.lightningkite.kiteui.models.Color
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.Theme
import com.lightningkite.kiteui.models.ThemeAndBack
import com.lightningkite.kiteui.models.ThemeDerivation
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.reactive.ReactiveContext
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.reactive
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.RView
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.dynamicTheme
import com.lightningkite.kiteui.views.expanding
import kotlin.jvm.JvmName

interface ButtonFormatting {
    val name: String
    val theme: ThemeDerivation
    val displayName: (ReactiveContext.() -> String)?
    val displayIcon: (ReactiveContext.() -> Icon?)?
    val displayIconColor: (ReactiveContext.() -> Color?)?
    val enabled: (ReactiveContext.() -> Boolean)?
}

data class ButtonAction(
    override val name: String,
    override val theme: ThemeDerivation = PrimaryButtonSemantic,
    override val displayName: (ReactiveContext.() -> String)? = null,
    override val displayIcon: (ReactiveContext.() -> Icon?)? = null,
    override val displayIconColor: (ReactiveContext.() -> Color?)? = null,
    override val enabled: (ReactiveContext.() -> Boolean)? = null,
    val action: suspend () -> Unit
) : Action by Action(name, action = action), ButtonFormatting {
    companion object {
        fun withUnread(
            name: String,
            theme: ThemeDerivation = PrimaryButtonSemantic,
            displayName: (ReactiveContext.() -> String)? = null,
            enabled: (ReactiveContext.() -> Boolean)? = null,
            unread: ReactiveContext.() -> Boolean,
            action: suspend () -> Unit
        ): ButtonAction = ButtonAction(name, theme, displayName, displayIcon = { if (unread()) Icon.circle else null }, displayIconColor = { AppColors.secondary.main }, enabled, action)
        fun withUnreadAndAction(
            name: String,
            theme: ThemeDerivation = PrimaryButtonSemantic,
            displayName: (ReactiveContext.() -> String)? = null,
            enabled: (ReactiveContext.() -> Boolean)? = null,
            actionIcon: ReactiveContext.() -> ActionImportance?,
            unread: ReactiveContext.() -> Boolean,
            action: suspend () -> Unit
        ) = ButtonAction(
            name, theme, displayName,
            displayIcon = { actionIcon()?.icon ?: if (unread()) Icon.circle else null },
            displayIconColor = { actionIcon()?.color ?: AppColors.secondary.main },
            enabled, action
        )
    }
}

private data class IconOverride(val color: Color) : ThemeDerivation {
    override fun invoke(theme: Theme): ThemeAndBack = theme.copy(
        id = "icn" + color.toAlphalessWeb().substringAfter('#'),
        iconOverride = color
    ).withoutBack
}

private fun RView.applyFormatting(formatting: ButtonFormatting) {
    if (formatting.displayIconColor != null)
        dynamicTheme {
            val color = formatting.displayIconColor?.invoke()
            if (color != null) formatting.theme + IconOverride(color)
            else formatting.theme
        }
    else themeChoice += formatting.theme

    specCenteredContent {
        row {
            centered - icon {
                description = formatting.name
                reactive {
                    val icon = formatting.displayIcon?.invoke()
                    exists = icon != null
                    source = icon
                }
            }
            centered - body {
                ::content { formatting.let { it.displayName?.invoke() ?: it.name } }
            }
        }
    }
}

private fun <T : ButtonFormatting> RView.applyReactiveFormatting(
    formatting: Readable<T?>,
    usingSpec: Boolean = true,
    withReactive: ReactiveContext.(T?) -> Unit,
) {
    dynamicTheme {
        formatting()?.let { a ->
            val iconColor = a.displayIconColor?.invoke()
            if (iconColor != null) a.theme + IconOverride(iconColor) else a.theme
        }
    }

    val content: RView.() -> ViewModifiable = {
        row {
            centered - icon {
                reactive {
                    val a = formatting()
                    val icon = a?.displayIcon?.invoke()
                    exists = icon != null
                    source = icon
                    description = a?.name
                }
            }
            centered - body {
                ::content { formatting()?.let { it.displayName?.invoke() ?: it.name } ?: "" }
            }
        }
    }
    if (usingSpec) specCenteredContent(setup = content) else stack { centered - content() }

    reactive {
        formatting().let {
            exists = it != null
            withReactive(it)
        }
    }
}

fun ViewWriter.mobileButtonRow(formats: Iterable<Readable<ButtonAction?>>) = row {
    spacing = AppDimensions.buttonRowSpacing
    val moreThanOneButton = shared { formats.count { it() != null } > 1 }
    reactive {
        clearChildren()
        if (moreThanOneButton()) {
            for (format in formats) {
                expanding - button { applyReactiveActionFormatting(format, usingSpec = false) }
            }
        }
        else {
            expanding - space()
            for (format in formats) {
                button { applyReactiveActionFormatting(format, usingSpec = true) }
            }
        }
    }
}

fun Button.applyActionFormatting(action: ButtonAction) {
    applyFormatting(action)

    if (action.enabled != null) this@applyActionFormatting::enabled.invoke(action.enabled)

    this@applyActionFormatting.action = action
}

fun ViewWriter.button(buttonAction: ButtonAction) = button { applyActionFormatting(buttonAction) }

fun Button.applyReactiveActionFormatting(action: Readable<ButtonAction?>, usingSpec: Boolean = true) {
    applyReactiveFormatting(action, usingSpec) {
        this@applyReactiveActionFormatting.action = it
    }

    ::enabled { action()?.let { it.enabled?.invoke() != false } ?: false }
}


data class LinkFormatting(
    override val name: String,
    override val theme: ThemeDerivation = PrimaryButtonSemantic,
    override val displayName: (ReactiveContext.() -> String)? = null,
    override val displayIcon: (ReactiveContext.() -> Icon?)? = null,
    override val displayIconColor: (ReactiveContext.() -> Color?)? = null,
    override val enabled: (ReactiveContext.() -> Boolean)? = null,
    val onNavigate: (suspend () -> Unit)? = null,
    val to: () -> Page
): ButtonFormatting

fun Link.applyFormatting(formatting: LinkFormatting) {
    applyFormatting(formatting)

    if (formatting.enabled != null) this@applyFormatting::enabled.invoke(formatting.enabled)

    formatting.onNavigate?.let { onNavigate(it) }

    to = formatting.to
}

fun ViewWriter.link(formatting: LinkFormatting) = link { applyFormatting(formatting) }

fun Link.applyReactiveFormatting(formatting: Readable<LinkFormatting?>) {
    applyReactiveFormatting(formatting) { read ->
        read?.onNavigate?.let { onNavigate(it) }
        to = read?.to
    }

    ::enabled { formatting()?.let { it.enabled?.invoke() != false } ?: false }
}